import { useMemo } from "react";

import { cn } from "@/lib/utils";
import type { TaxClosingPeriodItem } from "@/services/tax-closing-period";

import { MONTHS_INITIAL, MONTHS_PT } from "../constants";
import {
  PERIOD_STATUS_LABELS,
  TAX_CLOSING_PERIOD_CELL_KIND,
  type TaxClosingPeriodCellKind,
  type TaxClosingPeriodStatus,
} from "../status";

const CELL_COLOR_BY_KIND: Record<TaxClosingPeriodCellKind, string> = {
  [TAX_CLOSING_PERIOD_CELL_KIND.RELEASED]: "bg-green-500 text-white",
  [TAX_CLOSING_PERIOD_CELL_KIND.BLOCKED]: "bg-red-500 text-white",
  [TAX_CLOSING_PERIOD_CELL_KIND.CLOSED]: "bg-blue-500 text-white",
  [TAX_CLOSING_PERIOD_CELL_KIND.PLACEHOLDER]: "bg-amber-100 text-amber-700 border border-amber-300",
  [TAX_CLOSING_PERIOD_CELL_KIND.FUTURE]:
    "bg-muted/30 text-muted-foreground/40 border border-dashed border-muted-foreground/20",
};

const STATUS_LABEL_BY_KIND: Record<TaxClosingPeriodCellKind, string> = {
  ...PERIOD_STATUS_LABELS,
  [TAX_CLOSING_PERIOD_CELL_KIND.PLACEHOLDER]: "sem cadastro",
  [TAX_CLOSING_PERIOD_CELL_KIND.FUTURE]: "futuro",
};

const LEGEND_ENTRIES: Array<{ kind: TaxClosingPeriodCellKind; label: string }> = [
  { kind: TAX_CLOSING_PERIOD_CELL_KIND.RELEASED, label: PERIOD_STATUS_LABELS.RELEASED },
  { kind: TAX_CLOSING_PERIOD_CELL_KIND.BLOCKED, label: PERIOD_STATUS_LABELS.BLOCKED },
  { kind: TAX_CLOSING_PERIOD_CELL_KIND.PLACEHOLDER, label: "Sem cadastro" },
  { kind: TAX_CLOSING_PERIOD_CELL_KIND.CLOSED, label: PERIOD_STATUS_LABELS.CLOSED },
];

interface MiniYearCalendarProps {
  years: number[];
  months: TaxClosingPeriodItem[];
  currentMonth: number;
  currentYear: number;
  selectedYear: number;
  onYearClick: (year: number) => void;
}

export function MiniYearCalendar({
  years,
  months,
  currentMonth,
  currentYear,
  selectedYear,
  onYearClick,
}: MiniYearCalendarProps) {
  // Build a quick lookup: "year-month" → status
  const lookup = useMemo(() => {
    const map = new Map<string, TaxClosingPeriodStatus>();
    for (const m of months) {
      map.set(`${m.year}-${m.month}`, m.status);
    }
    return map;
  }, [months]);

  return (
    <div className="bg-card rounded-lg p-6 border shadow-sm flex flex-col">
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4">
        Visão Multi-Ano
      </h4>

      <div className="flex flex-col gap-3 flex-1">
        {years.map((year) => (
          <YearRow
            key={year}
            year={year}
            isSelected={year === selectedYear}
            currentYear={currentYear}
            currentMonth={currentMonth}
            lookup={lookup}
            onClick={() => onYearClick(year)}
          />
        ))}
      </div>

      <CalendarLegend />
    </div>
  );
}

// --------------------------------------------------------------------
// Cell kind resolver — no switch, no else-if
// --------------------------------------------------------------------
function resolveCellKind(
  status: TaxClosingPeriodStatus | undefined,
  isFuture: boolean,
): TaxClosingPeriodCellKind {
  if (status) return status;
  return isFuture
    ? TAX_CLOSING_PERIOD_CELL_KIND.FUTURE
    : TAX_CLOSING_PERIOD_CELL_KIND.PLACEHOLDER;
}

// --------------------------------------------------------------------
// Year row (label + 12 cells)
// --------------------------------------------------------------------
interface YearRowProps {
  year: number;
  isSelected: boolean;
  currentYear: number;
  currentMonth: number;
  lookup: Map<string, TaxClosingPeriodStatus>;
  onClick: () => void;
}

function YearRow({
  year,
  isSelected,
  currentYear,
  currentMonth,
  lookup,
  onClick,
}: YearRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 p-2 rounded-md transition-colors text-left",
        "hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-brand",
        isSelected && "bg-muted/60",
      )}
      aria-label={`Navegar para ${year}`}
    >
      <span
        className={cn(
          "text-xs font-bold w-10 shrink-0",
          isSelected ? "text-brand" : "text-muted-foreground",
        )}
      >
        {year}
      </span>
      <div className="flex gap-1">
        {Array.from({ length: 12 }, (_, i) => {
          const monthNum = i + 1;
          const status = lookup.get(`${year}-${monthNum}`);
          const isCurrent =
            year === currentYear && monthNum === currentMonth;
          const isFuture =
            year > currentYear ||
            (year === currentYear && monthNum > currentMonth);
          const kind = resolveCellKind(status, isFuture);

          return (
            <MonthCell
              key={monthNum}
              monthNum={monthNum}
              year={year}
              kind={kind}
              isCurrent={isCurrent}
            />
          );
        })}
      </div>
    </button>
  );
}

// --------------------------------------------------------------------
// Single colored cell
// --------------------------------------------------------------------
interface MonthCellProps {
  monthNum: number;
  year: number;
  kind: TaxClosingPeriodCellKind;
  isCurrent: boolean;
}

function MonthCell({ monthNum, year, kind, isCurrent }: MonthCellProps) {
  return (
    <div
      className={cn(
        "h-6 w-6 rounded-sm flex items-center justify-center text-[9px] font-bold transition-all",
        CELL_COLOR_BY_KIND[kind],
        isCurrent && "ring-2 ring-brand ring-offset-1",
      )}
      title={`${MONTHS_PT[monthNum - 1]}/${year} — ${STATUS_LABEL_BY_KIND[kind]}`}
    >
      {MONTHS_INITIAL[monthNum - 1]}
    </div>
  );
}

// --------------------------------------------------------------------
// Legend
// --------------------------------------------------------------------
function CalendarLegend() {
  return (
    <div className="mt-4 pt-4 border-t flex flex-wrap gap-3 text-[10px] text-muted-foreground">
      {LEGEND_ENTRIES.map((entry) => (
        <LegendDot
          key={entry.kind}
          className={CELL_COLOR_BY_KIND[entry.kind]}
          label={entry.label}
        />
      ))}
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn("h-2.5 w-2.5 rounded-sm", className)} />
      <span>{label}</span>
    </div>
  );
}
